import { AbstractConnector } from '@web3-react/abstract-connector'
import { ConnectorUpdate } from '@web3-react/types'

interface InjectedConnectorArguments {
  supportedChainIds?: number[]
}

export class InjectedConnector extends AbstractConnector {
  public readonly supportedChainIds?: number[]

  constructor({ supportedChainIds }: InjectedConnectorArguments = {}) {
    super({ supportedChainIds })
    this.supportedChainIds = supportedChainIds
  }

  private handleChainChanged = (chainId: string | number) => {
    this.emitUpdate({ chainId: this.parseChainId(chainId) })
  }

  private handleAccountsChanged = (accounts: string[]) => {
    if (accounts.length === 0) {
      this.emitDeactivate()
    } else {
      this.emitUpdate({ account: accounts[0] })
    }
  }

  private handleDisconnect = (error: Error) => {
    this.emitDeactivate()
  }

  private handleConnect = (connectInfo: any) => {
    this.emitUpdate({ chainId: this.parseChainId(connectInfo.chainId) })
  }

  public async activate(): Promise<ConnectorUpdate> {
    if (!window.ethereum) {
      throw new Error('No ethereum provider found')
    }

    if (window.ethereum.on) {
      window.ethereum.on('chainChanged', this.handleChainChanged)
      window.ethereum.on('accountsChanged', this.handleAccountsChanged)
      window.ethereum.on('disconnect', this.handleDisconnect)
      window.ethereum.on('connect', this.handleConnect)
    }

    let account
    try {
      account = await (window.ethereum as any)
        .request({ method: 'eth_requestAccounts' })
        .then((accounts: string[]) => accounts[0])
    } catch (error) {
      // 检查是否是用户拒绝连接
      if (error && typeof error === 'object' && 'code' in error && error.code === 4001) {
        throw new Error('User rejected connection')
      }
      // 检查是否是网络错误
      if (error && typeof error === 'object' && 'code' in error && error.code === -32603) {
        throw new Error('Network error occurred')
      }
      // 其他错误
      const errorMessage = error instanceof Error ? error.message : 'Failed to connect wallet'
      throw new Error(errorMessage)
    }

    const chainId = await (window.ethereum as any).request({ method: 'eth_chainId' })

    return { provider: window.ethereum, chainId: this.parseChainId(chainId), account }
  }

  public async getProvider(): Promise<any> {
    if (!window.ethereum) {
      throw new Error('No ethereum provider found')
    }
    return window.ethereum
  }

  public async getChainId(): Promise<number> {
    if (!window.ethereum) {
      throw new Error('No ethereum provider found')
    }

    const chainId = await (window.ethereum as any).request({ method: 'eth_chainId' })
    return this.parseChainId(chainId)
  }

  public async getAccount(): Promise<null | string> {
    if (!window.ethereum) {
      throw new Error('No ethereum provider found')
    }

    const accounts = await (window.ethereum as any).request({ method: 'eth_accounts' })
    return accounts[0] || null
  }

  public deactivate() {
    if (window.ethereum && window.ethereum.removeListener) {
      window.ethereum.removeListener('chainChanged', this.handleChainChanged)
      window.ethereum.removeListener('accountsChanged', this.handleAccountsChanged)
      window.ethereum.removeListener('disconnect', this.handleDisconnect)
      window.ethereum.removeListener('connect', this.handleConnect)
    }
  }

  public async isAuthorized(): Promise<boolean> {
    if (!window.ethereum) {
      return false
    }

    try {
      const accounts = await (window.ethereum as any).request({ method: 'eth_accounts' })
      return accounts.length > 0
    } catch {
      return false
    }
  }

  private parseChainId(chainId: string | number): number {
    return typeof chainId === 'number' ? chainId : parseInt(chainId, 16)
  }
}
